import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layout, Breadcrumb, Tree, Button, Space, Modal, Form, Input, Switch, InputNumber, message, Tabs, Select, Row, Col, Tag, Table, Image, Divider, Spin, Upload, Checkbox, Empty } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  PictureOutlined,
  DownOutlined, RightOutlined, LeftOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import Api from '../api';
import ThumbnailInput from '../components/ThumbnailInput.jsx';

const { Content } = Layout;

// Minimal Content Categories admin implementing core CRUD and tree view
const ContentCategories = () => {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [parentOptions, setParentOptions] = useState([]);
  const [form] = Form.useForm();
  const isMounted = useRef(false);
  const [selected, setSelected] = useState(null);
  const [videos, setVideos] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  // Inline Video modal state
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoEditing, setVideoEditing] = useState(null);
  const [videoForm] = Form.useForm();
  const watchedVideoUrl = Form.useWatch('video_url', videoForm);
  const [autoExtract, setAutoExtract] = useState(true);
  // Inline File modal state
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [fileEditing, setFileEditing] = useState(null);
  const [fileForm] = Form.useForm();

  const loadTree = useCallback(() => {
    setLoading(true);
    Api.get('/admin/content-categories/tree')
      .then(res => {
        if (!isMounted.current) return;
        const data = res?.data?.data || [];
        setTree(toAntTree(data));
        setParentOptions(flattenCategories(data));
      })
      .catch(() => { if (isMounted.current) message.error('Failed to load categories'); })
      .finally(() => { if (isMounted.current) setLoading(false); });
  }, []);

  useEffect(() => {
    isMounted.current = true;
    loadTree();
    return () => { isMounted.current = false; };
  }, [loadTree]);

  const toAntTree = useCallback((nodes) => {
    return (nodes || []).map(n => ({
      key: n.id,
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {n.image_url ? (
            <img alt="thumb" src={n.image_url} style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 6, flex: '0 0 auto' }} />
          ) : (
              <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 6,
                    background: '#fafafa',
                    border: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: '0 0 auto',
                  }}
              >
                <PictureOutlined style={{fontSize: 16, color: '#999'}}/>
              </div>
          )}
          <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
            <span style={{fontWeight: 600}}>{n.title}</span>
            {n.is_active === false && <Tag color="red" style={{marginLeft: 0}}>Inactive</Tag>}
          </div>
        </div>
      ),
      icon: null,
      raw: n,
      children: toAntTree(n.children || []),
    }));
  }, []);

  const flattenCategories = useCallback((nodes, arr = [], level = 0) => {
    (nodes || []).forEach(n => {
      arr.push({ label: `${'— '.repeat(level)}${n.title}`, value: n.id });
      flattenCategories(n.children || [], arr, level + 1);
    });
    return arr;
  }, []);

  const openCreate = useCallback((parentId = null) => {
    setEditing(null);
    setModalOpen(true);
    form.resetFields();
    form.setFieldsValue({ parent_id: parentId, is_active: true, order: 0 });
  }, [form]);

  const openEdit = useCallback((node) => {
    setEditing(node.raw);
    setModalOpen(true);
    form.setFieldsValue({
      title: node.raw.title,
      slug: node.raw.slug,
      description: node.raw.description,
      parent_id: node.raw.parent_id || null,
      order: node.raw.order || 0,
      is_active: node.raw.is_active !== false,
      image_input: node.raw.image_url || null,
    });
  }, [form]);

  const submit = useCallback((values) => {
    const method = editing ? 'put' : 'post';
    const url = '/admin/content-categories' + (editing ? `/${editing.id}` : '');
    const fd = new FormData();
    if (values.title) fd.append('title', values.title);
    if (values.slug) fd.append('slug', values.slug);
    if (values.description) fd.append('description', values.description);
    if (values.parent_id !== undefined && values.parent_id !== null) fd.append('parent_id', String(values.parent_id));
    if (values.order !== undefined && values.order !== null) fd.append('order', String(values.order));
    if (values.is_active !== undefined) fd.append('is_active', values.is_active ? '1' : '0');

    // handle image file upload; remove flag if cleared
    const imgVal = values.image_input;
    if (imgVal instanceof File) {
      fd.append('image', imgVal);
    } else if (imgVal === null && editing && editing.image_url) {
      // user cleared existing image
      fd.append('remove_image', '1');
    }

    Api[method](url, fd)
      .then(() => {
        message.success('Saved');
        setModalOpen(false);
        setEditing(null);
        loadTree();
      })
      .catch(() => message.error('Save failed'));
  }, [editing, loadTree]);

  const remove = useCallback((id) => {
    Modal.confirm({
      title: 'Delete category?',
      content: 'All subcategories and content will be removed. This action cannot be undone.',
      okType: 'danger',
      okText: 'Delete',
      cancelText: 'Cancel',
      onOk: () => Api.delete(`/admin/content-categories/${id}`)
        .then(() => { message.success('Deleted'); loadTree(); })
        .catch(() => message.error('Delete failed')),
    });
  }, [loadTree]);

  const renderTitle = useCallback((node) => {
    const raw = node.raw;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          {raw.image_url ? (
            <img alt="thumb" src={raw.image_url} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }} />
          ) : (
              <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 6,
                    background: '#fafafa',
                    border: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: '0 0 auto',
                  }}
              >
                <PictureOutlined style={{fontSize: 16, color: '#999'}}/>
              </div>
          )}
          <div style={{minWidth: 0}}>
            <div style={{fontWeight: 700}}>{raw.title} {raw.is_active === false &&
                <Tag color="red" style={{marginLeft: 6}}>Inactive</Tag>}</div>
            {raw.description && <div style={{
              color: '#888',
              fontSize: 12,
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              maxWidth: 420
            }}>{raw.description}</div>}
          </div>
        </div>
        <Space>
          <Button icon={<PlusOutlined/>} onClick={(e) => {
            e.stopPropagation();
            openCreate(raw.id);
          }}></Button>
          <Button icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); openEdit(node); }}></Button>
          <Button danger icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); remove(raw.id); }}></Button>
        </Space>
      </div>
    );
  }, [openCreate, openEdit, remove]);

  const treeData = useMemo(() => tree.map(n => ({ ...n, title: renderTitle(n), children: (n.children || []).map(c => ({ ...c, title: renderTitle(c) })) })), [tree, renderTitle]);

  // Define before onSelect to avoid temporal dead zone when used in dependencies
  const loadCategoryContent = useCallback((cid) => {
    setLoadingVideos(true);
    setLoadingFiles(true);
    Api.get(`/admin/content-videos?content_category_id=${cid}`)
      .then(res => {
        if (!isMounted.current) return;
        const d = res?.data?.data;
        const list = Array.isArray(d) ? d : (Array.isArray(d?.data) ? d.data : []);
        setVideos(list);
      })
      .catch(() => { if (isMounted.current) setVideos([]); })
      .finally(() => { if (isMounted.current) setLoadingVideos(false); });

    Api.get(`/admin/content-files?content_category_id=${cid}`)
      .then(res => {
        if (!isMounted.current) return;
        const d = res?.data?.data;
        const list = Array.isArray(d) ? d : (Array.isArray(d?.data) ? d.data : []);
        setFiles(list);
      })
      .catch(() => { if (isMounted.current) setFiles([]); })
      .finally(() => { if (isMounted.current) setLoadingFiles(false); });
  }, []);

  const onSelect = useCallback((keys, info) => {
    const node = info?.node?.raw;
    setSelected(node || null);
    if (node?.id) {
      loadCategoryContent(node.id);
    } else {
      setVideos([]); setFiles([]);
    }
  }, [loadCategoryContent]);

  const refreshSelectedContent = useCallback(() => {
    if (selected?.id) loadCategoryContent(selected.id);
  }, [selected, loadCategoryContent]);

  const openVideoCreate = useCallback(() => {
    if (!selected?.id) return message.warning('Select a category first');
    setVideoEditing(null);
    setVideoModalOpen(true);
    videoForm.resetFields();
    videoForm.setFieldsValue({ is_active: true, order: 0 });
    setAutoExtract(true);
  }, [selected, videoForm]);

  const openVideoEdit = useCallback((row) => {
    setVideoEditing(row);
    setVideoModalOpen(true);
    setAutoExtract(true);
    videoForm.setFieldsValue({
      title: row.title,
      video_url: row.video_url,
      description: row.description,
      order: row.order,
      is_active: row.is_active,
      thumbnail_input: row.thumbnail_url || row.thumbnail || null,
      allowed_memberships: Array.isArray(row.allowed_memberships) ? row.allowed_memberships : [],
    });
  }, [videoForm]);

  const submitVideo = useCallback((values) => {
    if (!selected?.id) return message.error('No category selected');
    const method = videoEditing ? 'put' : 'post';
    const url = '/admin/content-videos' + (videoEditing ? `/${videoEditing.id}` : '');
    const fd = new FormData();
    fd.append('content_category_id', String(selected.id));
    fd.append('title', values.title);
    fd.append('video_url', values.video_url);
    if (values.description) fd.append('description', values.description);
    if (values.order !== undefined && values.order !== null) fd.append('order', String(values.order));
    if (values.is_active !== undefined) fd.append('is_active', values.is_active ? '1' : '0');
    // allowed_memberships[] as multiple values if provided
    if (Array.isArray(values.allowed_memberships) && values.allowed_memberships.length > 0) {
      values.allowed_memberships.forEach(v => fd.append('allowed_memberships[]', v));
    }
    const thumbVal = values.thumbnail_input;
    if (thumbVal instanceof File) {
      fd.append('thumbnail', thumbVal);
    } else if (typeof thumbVal === 'string' && thumbVal) {
      fd.append('thumbnail_url', thumbVal);
    }

    Api[method](url, fd).then(() => {
      message.success('Saved');
      setVideoModalOpen(false); setVideoEditing(null);
      refreshSelectedContent();
    }).catch((e) => {
      const errs = e?.response?.data?.errors;
      if (errs && typeof errs === 'object') {
        videoForm.setFields(Object.entries(errs).map(([name, msgs]) => ({ name, errors: Array.isArray(msgs) ? msgs : [String(msgs)] })));
      }
      message.error(e?.response?.data?.message || 'Save failed');
    });
  }, [selected, videoEditing, refreshSelectedContent, videoForm]);

  const removeVideo = useCallback((id) => {
    Modal.confirm({
      title: 'Delete video?',
      content: 'This action cannot be undone. The video will be permanently removed.',
      okType: 'danger',
      okText: 'Delete',
      cancelText: 'Cancel',
      onOk: () => Api.delete(`/admin/content-videos/${id}`)
        .then(() => { message.success('Deleted'); refreshSelectedContent(); })
        .catch(() => message.error('Delete failed')),
    });
  }, [refreshSelectedContent]);

  const toggleVideoActive = useCallback((row) => {
    const prev = row.is_active;
    setVideos(videos.map(v => v.id === row.id ? { ...v, is_active: !prev } : v));
    Api.post(`/admin/content-videos/${row.id}/toggle-active`).catch(() => {
      message.error('Toggle failed');
      setVideos(videos.map(v => v.id === row.id ? { ...v, is_active: prev } : v));
    });
  }, [videos]);

  const videoColumns = useMemo(() => [
    { title: 'Thumbnail', dataIndex: 'thumbnail_url', width: 130, render: (_, r) => {
      const src = r.thumbnail_url || r.thumbnail || null;
      return src ? <Image width={120} height={68} style={{ objectFit: 'cover' }} src={src} /> : null;
    } },
    { title: 'Title', dataIndex: 'title', ellipsis: true },
    { title: 'Order', dataIndex: 'order', width: 80 },
    { title: 'Status', dataIndex: 'is_active', width: 100, render: v => v ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag> },
    { title: 'Access', key: 'access', width: 120, render: (_, row) => {
      const list = Array.isArray(row.allowed_memberships) ? row.allowed_memberships : [];
      const hasLimited = list.length > 0;
      // No need for a label map here; a count is enough for quick scanning.
      const title = hasLimited ? `${list.length} selected` : 'All memberships';
      return hasLimited ? <Tag color="gold" title={title}>Limited</Tag> : <Tag color="blue" title={title}>All</Tag>;
    } },
    { title: 'Actions', key: 'actions', width: 100, render: (_, row) => (
      <Space>
        <Button icon={<EditOutlined />} onClick={() => openVideoEdit(row)}></Button>
        <Button danger icon={<DeleteOutlined />} onClick={() => removeVideo(row.id)}></Button>
      </Space>
    ) },
  ], [openVideoEdit, toggleVideoActive, removeVideo]);

  const openFileCreate = useCallback(() => {
    if (!selected?.id) return message.warning('Select a category first');
    setFileEditing(null);
    setFileModalOpen(true);
    fileForm.resetFields();
    fileForm.setFieldsValue({ is_active: true, order: 0 });
  }, [selected, fileForm]);

  const openFileEdit = useCallback((row) => {
    setFileEditing(row);
    setFileModalOpen(true);
    fileForm.setFieldsValue({
      title: row.title,
      description: row.description,
      order: row.order,
      is_active: row.is_active,
      allowed_memberships: Array.isArray(row.allowed_memberships) ? row.allowed_memberships : [],
    });
  }, [fileForm]);

  const submitFile = useCallback((values) => {
    if (!selected?.id) return message.error('No category selected');
    const method = fileEditing ? 'put' : 'post';
    const url = '/admin/content-files' + (fileEditing ? `/${fileEditing.id}` : '');
    const data = new FormData();
    data.append('content_category_id', String(selected.id));
    data.append('title', values.title);
    if (values.description) data.append('description', values.description);
    if (values.order !== undefined && values.order !== null) data.append('order', String(values.order));
    if (values.is_active !== undefined) data.append('is_active', values.is_active ? '1' : '0');
    if (Array.isArray(values.allowed_memberships) && values.allowed_memberships.length > 0) {
      values.allowed_memberships.forEach(v => data.append('allowed_memberships[]', v));
    }
    if (!fileEditing) {
      const fl = Array.isArray(values.file) ? values.file : [];
      if (fl[0]?.originFileObj) data.append('file', fl[0].originFileObj);
    }
    Api[method](url, data).then(() => {
      message.success('Saved');
      setFileModalOpen(false); setFileEditing(null);
      refreshSelectedContent();
    }).catch((e) => {
      const errs = e?.response?.data?.errors;
      if (errs && typeof errs === 'object') {
        fileForm.setFields(Object.entries(errs).map(([name, msgs]) => ({ name, errors: Array.isArray(msgs) ? msgs : [String(msgs)] })));
      }
      message.error(e?.response?.data?.message || 'Save failed');
    });
  }, [selected, fileEditing, refreshSelectedContent, fileForm]);

  const removeFile = useCallback((id) => {
    Modal.confirm({
      title: 'Delete file?',
      content: 'This action cannot be undone. The file will be permanently removed.',
      okType: 'danger',
      okText: 'Delete',
      cancelText: 'Cancel',
      onOk: () => Api.delete(`/admin/content-files/${id}`)
        .then(() => { message.success('Deleted'); refreshSelectedContent(); })
        .catch(() => message.error('Delete failed')),
    });
  }, [refreshSelectedContent]);

  const toggleFileActive = useCallback((row) => {
    const prev = row.is_active;
    setFiles(files.map(v => v.id === row.id ? { ...v, is_active: !prev } : v));
    Api.post(`/admin/content-files/${row.id}/toggle-active`).catch(() => {
      message.error('Toggle failed');
      setFiles(files.map(v => v.id === row.id ? { ...v, is_active: prev } : v));
    });
  }, [files]);

  const fileColumns = useMemo(() => [
    { title: 'Title', dataIndex: 'title', ellipsis: true },
    { title: 'Type', dataIndex: 'file_type', width: 120 },
    { title: 'Size', dataIndex: 'file_size', width: 120, render: (b) => b ? `${(b/1024/1024).toFixed(2)} MB` : '-' },
    { title: 'Order', dataIndex: 'order', width: 80 },
    { title: 'Status', dataIndex: 'is_active', width: 100, render: v => v ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag> },
    { title: 'Access', key: 'access', width: 120, render: (_, row) => {
      const list = Array.isArray(row.allowed_memberships) ? row.allowed_memberships : [];
      const hasLimited = list.length > 0;
      const title = hasLimited ? `${list.length} selected` : 'All memberships';
      return hasLimited ? <Tag color="gold" title={title}>Limited</Tag> : <Tag color="blue" title={title}>All</Tag>;
    } },
    { title: 'Actions', key: 'actions', width: 240, render: (_, row) => (
      <Space>
        <Button size="small" type="link" icon={<EditOutlined />} onClick={() => openFileEdit(row)}>Edit</Button>
        <Button size="small" onClick={() => toggleFileActive(row)}>{row.is_active ? 'Deactivate' : 'Activate'}</Button>
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeFile(row.id)}>Delete</Button>
      </Space>
    ) },
  ], [openFileEdit, toggleFileActive, removeFile]);

  // Inline video/file handlers (converted to stable callbacks above)

  return (
      <Content style={{margin: '0 16px'}}>
        <Breadcrumb style={{ margin: '16px 0' }}
          items={[
            { title: <Link to="/">Dashboard</Link> },
            { title: 'Content' },
            { title: 'Categories' },
          ]}
        />
        <div className="site-layout-background" style={{ padding: 24, minHeight: 360 }}>
          <Space style={{ marginBottom: 12 }} wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate(null)}>Add Root Category</Button>
            <Button icon={<ReloadOutlined />} onClick={loadTree} loading={loading}>Reload</Button>
          </Space>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12} lg={10} xl={9} xxl={8}>
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 8, maxHeight: '70vh', minHeight: '40vh', overflow: 'auto' }}>
                <Tree treeData={treeData}   switcherIcon={(props) =>
                    props.isLeaf ? (
                        <RightOutlined />
                    ) : (
                        <DownOutlined />
                    )
                } defaultExpandAll onSelect={onSelect} />
              </div>
            </Col>
            <Col xs={24} md={12} lg={14} xl={15} xxl={16}>
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 16, minHeight: 200 }}>
                {selected ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      {selected.image_url ? (
                        <img alt="thumb" src={selected.image_url} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8 }} />
                      ) : (
                          <div
                              style={{
                                width: 48,
                                height: 48,
                                borderRadius: 6,
                                background: '#fafafa',
                                border: '1px solid #f0f0f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: '0 0 auto',
                              }}
                          >
                            <PictureOutlined style={{fontSize: 16, color: '#999'}}/>
                          </div>
                      )}
                      <div>
                        <div style={{fontSize: 18, fontWeight: 800}}>{selected.title}</div>
                        {selected.description && <div style={{color: '#888'}}>{selected.description}</div>}
                      </div>
                    </div>
                    <Divider/>
                    <Row gutter={16}>
                      <Col span={24}>
                        <Tabs
                            defaultActiveKey="videos"
                            items={[
                              {
                                key: 'videos',
                                label: (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <h3 style={{ margin: 0 }}>
                                        Videos {loadingVideos ? <Spin size="small" /> : `(${videos.length})`}
                                      </h3>
                                      <Button
                                          type="default"
                                          onClick={openVideoCreate}
                                          disabled={!selected}
                                          icon={<PlusOutlined/>}
                                      >
                                      </Button>
                                    </div>
                                ),
                                children: (
                                    <Table
                                        rowKey="id"
                                        size="small"
                                        dataSource={videos}
                                        columns={videoColumns}
                                        pagination={false}
                                        loading={loadingVideos}
                                        scroll={{ x: true }}
                                        locale={{ emptyText: <Empty description="No videos" /> }}
                                    />
                                ),
                              },
                              {
                                key: 'files',
                                label: (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <h3 style={{ margin: 0 }}>
                                        Files {loadingFiles ? <Spin size="small" /> : `(${files.length})`}
                                      </h3>
                                      <Button
                                          type="default"
                                          onClick={openFileCreate}
                                          disabled={!selected}
                                          icon={<PlusOutlined/>}
                                      >
                                      </Button>
                                    </div>
                                ),
                                children: (
                                    <Table
                                        rowKey="id"
                                        size="small"
                                        dataSource={files}
                                        columns={fileColumns}
                                        pagination={false}
                                        loading={loadingFiles}
                                        scroll={{ x: true }}
                                        locale={{ emptyText: <Empty description="No files" /> }}
                                    />
                                ),
                              }
                            ]}
                        />
                      </Col>
                    </Row>
                  </>
                ) : (
                  <div style={{ padding: 24 }}>
                    <Empty description="Select a category from the tree to view its videos and files" />
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>

      <Modal title={editing ? 'Edit Category' : 'Add Category'} open={modalOpen} onCancel={() => { setModalOpen(false); setEditing(null); }} footer={null} destroyOnClose width={720} style={{ maxWidth: '96vw' }} bodyStyle={{ maxHeight: '70vh', overflow: 'auto' }}>
        <Form form={form} layout="vertical" onFinish={submit}>
          <Form.Item label="Title" name="title" rules={[{ required: true, message: 'Title is required' }]}>
            <Input placeholder="Category title" />
          </Form.Item>
          <Form.Item label="Slug" name="slug">
            <Input placeholder="auto-generated if left empty" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="image_input" valuePropName="value">
            <ThumbnailInput label={null} showUrlOption={false} />
          </Form.Item>
          <Form.Item label="Parent category" name="parent_id">
            <Select allowClear options={parentOptions} placeholder="Root" showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item label="Order" name="order" initialValue={0}>
            <InputNumber min={0} style={{ width: 120 }} />
          </Form.Item>
          <Form.Item label="Active" name="is_active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button onClick={() => { setModalOpen(false); setEditing(null); }}>Cancel</Button>
              <Button type="primary" htmlType="submit">Save</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Inline Video Modal */}
      <Modal title={videoEditing ? 'Edit Video' : 'Add Video'} open={videoModalOpen} onCancel={() => { setVideoModalOpen(false); setVideoEditing(null); }} footer={null} destroyOnClose width={720} style={{ maxWidth: '96vw' }} bodyStyle={{ maxHeight: '70vh', overflow: 'auto' }}>
        <Form form={videoForm} layout="vertical" onFinish={submitVideo}>
          <Form.Item label="Title" name="title" rules={[{ required: true, message: 'Title is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Video URL" name="video_url" rules={[{ required: true, message: 'Video URL is required' }]}>
            <Input placeholder="YouTube/Vimeo URL" />
          </Form.Item>
          <Form.Item name="thumbnail_input" valuePropName="value">
            <ThumbnailInput label={null} showUrlOption={true} autoExtractYouTube={true} videoUrl={watchedVideoUrl} />
          </Form.Item>
          <Form.Item label="Allowed memberships" name="allowed_memberships">
            <Select
              mode="multiple"
              allowClear
              placeholder="All memberships (leave empty)"
              options={[
                { label: 'VIP', value: 'vip' },
                { label: 'Fitlap+', value: 'premium' },
                // { label: 'Trial', value: 'trial' },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Checkbox checked={autoExtract} onChange={e => setAutoExtract(e.target.checked)} disabled>
              Auto-extract from YouTube (backend supported when no thumbnail provided)
            </Checkbox>
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="Order" name="order" initialValue={0}>
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item label="Active" name="is_active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          {/* Video embed preview */}
          {(() => {
            const url = watchedVideoUrl;
            let embed = null;
            if (url) {
              const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\?\/]+)/);
              if (yt && yt[1]) embed = `https://www.youtube.com/embed/${yt[1]}`;
              const vm = url.match(/vimeo\.com\/(\d+)/);
              if (!embed && vm && vm[1]) embed = `https://player.vimeo.com/video/${vm[1]}`;
            }
            return embed ? (
              <div style={{ marginBottom: 16 }}>
                <iframe title="video-preview" width="100%" height="300" src={embed} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            ) : null;
          })()}
          <Form.Item>
            <Space>
              <Button onClick={() => { setVideoModalOpen(false); setVideoEditing(null); }}>Cancel</Button>
              <Button type="primary" htmlType="submit">Save</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Inline File Modal */}
      <Modal title={fileEditing ? 'Edit File' : 'Add File'} open={fileModalOpen} onCancel={() => { setFileModalOpen(false); setFileEditing(null); }} footer={null} destroyOnClose width={720} style={{ maxWidth: '96vw' }} bodyStyle={{ maxHeight: '70vh', overflow: 'auto' }}>
        <Form form={fileForm} layout="vertical" onFinish={submitFile}>
          <Form.Item label="Title" name="title" rules={[{ required: true, message: 'Title is required' }]}>
            <Input />
          </Form.Item>
          {!fileEditing && (
            <Form.Item label="File" name="file" valuePropName="fileList" getValueFromEvent={e => (Array.isArray(e) ? e : e && e.fileList)} rules={[{ required: true, message: 'File is required' }]}>
              <Upload beforeUpload={() => false} maxCount={1}>
                <Button>Select file</Button>
              </Upload>
            </Form.Item>
          )}
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="Allowed memberships" name="allowed_memberships">
            <Select
              mode="multiple"
              allowClear
              placeholder="All memberships (leave empty)"
              options={[
                { label: 'Inactive', value: 'inactive' },
                { label: 'Regular', value: 'regular' },
                { label: 'Recurring', value: 'recurring' },
                { label: 'VIP', value: 'vip' },
                { label: 'Premium', value: 'premium' },
                { label: 'Trial', value: 'trial' },
                { label: 'Free', value: 'free' },
              ]}
            />
          </Form.Item>
          <Form.Item label="Order" name="order" initialValue={0}>
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item label="Active" name="is_active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button onClick={() => { setFileModalOpen(false); setFileEditing(null); }}>Cancel</Button>
              <Button type="primary" htmlType="submit">Save</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Content>
  );
};

export default ContentCategories;
